import InfoIcon from 'assets/icons/landing/info.svg?component'
import InfoIconGreen from 'assets/icons/landing/info-green.svg?component'
import BodyText from 'components/UI/Landing/body-text'
import PrimaryButton from 'components/UI/Landing/button-primary'
import LandingContainer from 'components/UI/Landing/container'
import LandingSlider from 'components/UI/Landing/landing-slider'
import SectionTitle from 'components/UI/Landing/section-title'
import SubTitle from 'components/UI/Landing/subtitle'
import Typography from 'components/UI/Typography'
import Heading from 'components/UI/Typography/Heading'
import { useAppDispatch, useAppSelector } from 'hooks'
import { useEffect, useMemo, useReducer } from 'react'
import { useTranslation } from 'react-i18next'
import { createSearchParams, useNavigate } from 'react-router-dom'
import { useCalculateCreditMutation, useGetCreditProgramsQuery } from 'redux/api/creditApi'
import { getLoanInformation, setLoanInformation } from 'redux/store/slices/loanSlice'
import { RepaymentMethods } from 'share/enums/payment'
import { LoanState } from 'share/types/model/loan'
import tw from 'twin.macro'
import { numberWithSpaces } from 'utils'
import { pathnames } from 'utils/constants'
import LoanCalculator from 'utils/loan-calculator'
import { getLoanPurposeText, getProgramName } from 'utils/translations'

type CalculatorState = {
  loanPercent: number | null
  gesvPercent: number | null
} & LoanState

const initialState: CalculatorState = {
  amount: 1000,
  term: 0,
  loanPercent: null,
  gesvPercent: null,
  minTerm: 0,
  maxTerm: 0,
  minAmount: 0,
  maxAmount: 10_000_000
}

const reducer = (state: CalculatorState, newState: Partial<CalculatorState>) => ({
  ...state,
  ...newState
})

const CalculatorView = () => {
  // Tools
  const { t } = useTranslation('landing')
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  // Selectors
  const { loanRepaymentMethod } = useAppSelector(getLoanInformation)

  // Queries
  const { data, isSuccess } = useGetCreditProgramsQuery()

  // Mutations
  const [calculateCredit, { isLoading: isCalculateCreditLoading }] = useCalculateCreditMutation()

  // State
  const [{ minTerm, amount, term, maxTerm, minAmount, maxAmount }, setState] = useReducer(reducer, initialState)

  // Computed values
  const filteredPrograms = useMemo(() => {
    if (!data) return
    return data.data.filter(program => program.program_code === 'OWN_PROGRAM')
  }, [data])

  const minInterestRate = useMemo(() => {
    if (!filteredPrograms) return
    return Math.min(...filteredPrograms.map(program => program.client_interest_rate))
  }, [filteredPrograms])

  const matchingProgram = useMemo(() => {
    if (!term || !amount || !filteredPrograms) return
    const program = filteredPrograms.sort((a, b) => a.priority - b.priority)?.[0]
    if (!program) return
    return program
  }, [amount, filteredPrograms, term])

  const calculatorResults = useMemo(() => {
    if (!amount || !term || !minInterestRate) return
    const calc = new LoanCalculator(amount, term, minInterestRate, loanRepaymentMethod)
    return calc.getCalcResults()
  }, [amount, loanRepaymentMethod, minInterestRate, term])

  // Handlers
  const handleRepaymentMethodChange = (value: RepaymentMethods) => {
    dispatch(setLoanInformation({ loanRepaymentMethod: value }))
  }

  const handleVerifyCalculation = async () => {
    try {
      const res = await calculateCredit({
        repayment_method: loanRepaymentMethod,
        amount: amount as number,
        period: term as number
      }).unwrap()
      navigate({
        pathname: pathnames['create-lead'],
        search: createSearchParams({
          interest_rate: String(matchingProgram?.client_interest_rate),
          period: String(term),
          amount: String(amount),
          program: String(matchingProgram?.id),
          gesv: res.gesv.toString(),
          monthly_payment: res.payment.toString(),
          repayment_method: loanRepaymentMethod
        }).toString()
      })
    } catch (err) {
      console.log(err)
    }
  }

  // Effects
  useEffect(() => {
    if (isSuccess && data?.data?.length !== 0) {
      setState({
        term: data?.minMonth,
        minTerm: data?.minMonth,
        maxTerm: data?.maxMonth,
        amount: data?.minAmount,
        minAmount: data?.minAmount,
        maxAmount: data?.maxAmount
      })
    }
  }, [data?.data?.length, data?.maxAmount, data?.maxMonth, data?.minAmount, data?.minMonth, isSuccess])

  return (
    <div className='pb-16 relative z-10'>
      <LandingContainer className='flex flex-col gap-4'>
        <div
          className={`rounded-2xl p-4 bg-[var(--bg-landing-card)] md:p-12 shadow-[0px 5px 20px rgba(255, 255, 255, 0.05)]`}
        >
          <SectionTitle title={t('mobile-menu.calc-section')} />
          <div className='grid grid-cols-1 lg:grid-cols-7 lg:divide-x lg:divide-[var(--bg-track)] mt-8  justify-between '>
            <div className='pb-4 space-y-6 col-span-4 lg:pr-12 lg:pb-0'>
              <div>
                <SubTitle title={t('loan.amount', { ns: 'common' })} className='text-sm mb-2' />
                <p className='font-bold text-[32px] mb-[17.5px] leading-[40px] text-[var(--text-landing-green)]'>
                  {numberWithSpaces(amount)} ₸
                </p>
                {minAmount !== null && maxAmount !== null && amount !== null ? (
                  <div className='px-2'>
                    <LandingSlider
                      twStyle={tw`mt-4`}
                      min={minAmount}
                      max={maxAmount}
                      step={100_000}
                      value={+amount}
                      renderMinText={min => `${numberWithSpaces(min)} ₸`}
                      renderMaxText={max => `${numberWithSpaces(max)} ₸`}
                      onChange={val => setState({ amount: val })}
                    />
                  </div>
                ) : null}
              </div>
              <div>
                <SubTitle title={t('calculator-section.amount-month')} className='text-sm mb-2' />
                <p className='font-bold text-[32px] leading-10 text-[var(--text-landing-green)] mb-[17.5px]'>{term}</p>
                {minTerm !== null && maxTerm !== null && term !== null ? (
                  <div className='px-2'>
                    <LandingSlider
                      twStyle={tw`mt-4`}
                      min={minTerm}
                      max={maxTerm}
                      step={1}
                      value={+term}
                      renderMinText={min => t('calculator.period-limit', { ns: 'common', period: min })}
                      renderMaxText={max => t('calculator.period-limit', { ns: 'common', period: max })}
                      onChange={val => setState({ term: val })}
                    />
                  </div>
                ) : null}
                <div className='mt-[24px] hidden lg:block'>
                  <Typography variant='caption' twStyle={tw`text-[var(--text-landing-subtitle)] mb-2`}>
                    {t('loan.purpose', { ns: 'common' })}
                  </Typography>
                  <Heading as='h3' variant='subtitle'>
                    {t(getLoanPurposeText(matchingProgram?.loan_purpose_verbose), { ns: 'common' })}
                  </Heading>
                </div>
                <div className='mt-[24px]  hidden lg:block'>
                  <Typography variant='caption' twStyle={tw`text-[var(--text-landing-subtitle)] mb-2`}>
                    {t('programs.title', { ns: 'common' })}
                  </Typography>
                  <Heading as='h3' variant='subtitle'>
                    {t(getProgramName(matchingProgram?.program_code), { ns: 'common' })}
                  </Heading>
                </div>

                <div className='hidden lg:flex items-center gap-[9.67px] px-3 py-3 rounded-lg text-muted bg-[var(--bg-warning-green)] mt-[24px]'>
                  <div className='shrink-0'>
                    <InfoIconGreen />
                  </div>
                  <BodyText
                    text={t('calculator-section.warning-text-1')}
                    className='text-[12px] leading-[140%] text-[#20B553]'
                  />
                </div>
              </div>
            </div>
            <div className='pt-4 col-span-3 lg:pl-12 lg:pt-0 flex flex-col'>
              <SubTitle title={t('loan.repayment-method', { ns: 'common' })} className='text-sm' />
              <div
                css={[
                  tw`rounded-[16px] p-1 mb-[24px] bg-[var(--bg-landing-toggle)] text-[var(--text-title)] flex gap-2 z-10 mt-[9px]`
                ]}
              >
                <button
                  type='button'
                  css={[
                    tw`w-full py-[14px] text-sm rounded-xl shadow-[0px 5px 20px rgba(0, 0, 0, 0.05)] text-[var(--text-title)] font-medium`,
                    loanRepaymentMethod === RepaymentMethods.ANNUITY && tw`bg-[var(--bg-landing-toggle-button)]`
                  ]}
                  onClick={() => handleRepaymentMethodChange(RepaymentMethods.ANNUITY)}
                >
                  {t('repayment-methods.annuity', { ns: 'common' })}
                </button>
                <button
                  type='button'
                  css={[
                    tw`w-full py-[14px] text-sm rounded-xl shadow-[0px 5px 20px rgba(0, 0, 0, 0.05)] font-medium`,
                    loanRepaymentMethod === RepaymentMethods.EQUAL_INSTALMENTS &&
                      tw`bg-[var(--bg-landing-toggle-button)]`
                  ]}
                  onClick={() => handleRepaymentMethodChange(RepaymentMethods.EQUAL_INSTALMENTS)}
                >
                  {t('repayment-methods.equal-instalments', { ns: 'common' })}
                </button>
              </div>
              <div className='flex flex-col gap-x-4'>
                <div>
                  <Typography
                    variant='caption'
                    twStyle={tw`text-grey-dark mb-2 lg:text-[14px] lg:leading-[22px] text-[var(--text-landing-subtitle)]`}
                  >
                    {t('loan.monthly-payment', { ns: 'common' })}
                  </Typography>
                  <Heading as='h3' variant='subtitle' twStyle={tw`lg:text-[32px] lg:leading-[40px]`}>
                    {numberWithSpaces(calculatorResults?.monthlyPayment?.toFixed(0))} ₸
                  </Heading>
                </div>
                <div tw='mt-4'>
                  <Typography
                    variant='caption'
                    twStyle={tw`text-grey-dark mb-2 lg:text-[14px] lg:leading-[22px] text-[var(--text-landing-subtitle)]`}
                  >
                    {t('loan.overpayment', { ns: 'common' })}
                  </Typography>
                  <Heading as='h3' variant='subtitle' twStyle={tw`lg:text-[32px] lg:leading-[40px]`}>
                    {numberWithSpaces(calculatorResults?.overpayment?.toFixed(0))} ₸
                  </Heading>
                </div>
                <div className='mt-[24px] flex items-center gap-[24px]'>
                  <div>
                    <Typography
                      variant='caption'
                      twStyle={tw`text-[var(--text-landing-subtitle)] mb-2 lg:text-[14px] lg:leading-[22px]`}
                    >
                      {t('loan.interest-rate-full', { ns: 'common' })}
                    </Typography>
                    <Heading
                      as='h3'
                      variant='subtitle'
                      twStyle={tw`text-[20px] leading-[140%] text-[var(--text-title)]`}
                    >
                      {minInterestRate} %
                    </Heading>
                  </div>
                  <div>
                    <Typography
                      variant='caption'
                      twStyle={tw`text-[var(--text-landing-subtitle)] tracking-[0.25%] mb-2 lg:text-[14px] lg:leading-[22px]`}
                    >
                      {t('loan.gesv', { ns: 'common' })}
                    </Typography>
                    <Heading
                      as='h3'
                      variant='subtitle'
                      twStyle={tw`text-[20px] leading-[140%] text-[var(--text-title)]`}
                    >
                      {calculatorResults?.gesv} %
                    </Heading>
                  </div>
                </div>
              </div>
              <div className='mt-[28px] lg:hidden'>
                <Typography variant='caption' twStyle={tw`text-[var(--text-landing-subtitle)] mb-1`}>
                  {t('loan.purpose', { ns: 'common' })}
                </Typography>
                <Heading as='h3' variant='subtitle'>
                  {t(getLoanPurposeText(matchingProgram?.loan_purpose_verbose), { ns: 'common' })}
                </Heading>
              </div>
              <div className='mt-[28px] lg:hidden'>
                <Typography variant='caption' twStyle={tw`text-[var(--text-landing-subtitle)] mb-1`}>
                  {t('programs.title', { ns: 'common' })}
                </Typography>
                <Heading as='h3' variant='subtitle'>
                  {t(getProgramName(matchingProgram?.program_code), { ns: 'common' })}
                </Heading>
              </div>
              <div className=' mt-8 lg:mt-[95px] xl:mt-[80px] flex flex-col justify-between gap-4 mb-[23px]'>
                <div className='flex lg:hidden items-center gap-[9.67px] px-3 py-3 rounded-lg text-muted bg-[var(--bg-warning-green)]'>
                  <div className='shrink-0'>
                    <InfoIconGreen />
                  </div>
                  <BodyText
                    text={t('calculator-section.warning-text-1')}
                    className='text-[12px] leading-[140%] text-[#20B553]'
                  />
                </div>
                <PrimaryButton
                  className='py-[14px] xl:py-[23px] xl:px-[41px]  font-semibold text-[18px] leading-[24px] whitespace-nowrap'
                  disabled={isCalculateCreditLoading}
                  onClick={handleVerifyCalculation}
                >
                  {t('mobile-menu.btn-text')}
                </PrimaryButton>
              </div>
              <div
                className={`flex items-center gap-3 px-0 md:px-3 py-3 rounded-lg text-muted md:bg-[var(--bg-landing-warning)] bg-none mt-auto`}
              >
                <div className='shrink-0 hidden md:block'>
                  <InfoIcon />
                </div>
                <BodyText text={t('calculator-section.warning-text-2')} className='text-[12px] leading-[140%] ' />
              </div>
            </div>
          </div>
        </div>
      </LandingContainer>
    </div>
  )
}

export default CalculatorView
